/**
 * Prefetch Media Page Data
 *
 * This function makes the same API call as MediaPage without storing the result.
 * It populates the HTTP cache so that when the user navigates to the media page,
 * the data loads instantly from cache instead of making a fresh API call.
 *
 * Usage:
 * - Call from HomePage after initial render
 * - Call on nav link hover for immediate prefetch
 * - Non-blocking: Doesn't affect current page performance
 */

import { getAuthClient } from '../../utils/auth';
import { buildYourTourApiUrl } from '../../utils/ApiHelpers';
import { getDataVersion } from '../../utils/dataVersions';

const auth = getAuthClient();

let prefetchInProgress = false;
let prefetchComplete = false;
let hasUserVisitedMedia = false;

// Call this from MediaPage when it loads to mark that user has visited
export function markMediaVisited() {
  hasUserVisitedMedia = true;
}

export async function prefetchMedia(urlSlug, mode) {
  // Don't prefetch if user has already visited media page (already cached)
  if (hasUserVisitedMedia) {
    return;
  }

  // Don't prefetch if already in progress or complete
  if (prefetchInProgress || prefetchComplete) {
    return;
  }

  // Don't prefetch if required params are missing
  if (!urlSlug) {
    console.warn('[Prefetch] Missing urlSlug, skipping media prefetch');
    return;
  }

  prefetchInProgress = true;

  try {
    // Use the same custom endpoint + version param as MediaPage
    const version = await getDataVersion( urlSlug, 'media', undefined, mode );
    const params = {
      ...( mode ? { mode } : {} ),
      ...( version ? { v: version } : {} ),
    };
    const url = buildYourTourApiUrl( `/your_tour_api/media_page/${urlSlug}`, params );
    await auth.fetchWithAuthentication(url, 'GET', null);

    prefetchComplete = true;
  } catch (error) {
    console.warn('[Prefetch] Media prefetch failed (non-critical):', error);
    // Don't mark as complete on error so it can retry
  } finally {
    prefetchInProgress = false;
  }
}

// Reset prefetch state (useful for testing or when community changes)
export function resetPrefetch() {
  prefetchInProgress = false;
  prefetchComplete = false;
  hasUserVisitedMedia = false;
}

